import { hoursToMilliseconds, minutesToMilliseconds } from 'date-fns';

import { extractPropertyUidsFromMultiSelect } from 'components/forms/domain/property/PropertyMultiSelectFilterField/PropertyMultiSelectFilterField.utils';
import {
  isNotSelectAll,
  isSelectAll,
} from 'components/forms/multiSelectField/MultiSelectField.constants';
import { BaseOption } from 'components/forms/multiSelectField/MultiSelectField.types';
import { DayOfWeek } from 'models/DayOfWeek';
import { LeadChannel } from 'models/Leads';
import {
  AllTriggerConditions,
  ConditionType,
  GetTriggerOptionsResponseTO,
  GuestsNumberOperator,
  IsCompleted,
  IsSigned,
  StayLengthOperator,
  TimeOperator,
  TimeType,
  TriggerActionType,
  TriggerCondition,
  TriggerConditionForChannel,
  TriggerConditionForDaysOfWeek,
  TriggerConditionForGuestNumber,
  TriggerConditionForNegation,
  TriggerConditionForProperty,
  TriggerConditionForStay,
  TriggerConditionForStayLength,
  TriggerEventType,
  TriggerJobTiming,
  TriggerType,
  TriggersCreatePayload,
} from 'models/automation/Triggers';
import { getRecipientUidAndTypeFromValue } from '../TriggersTab.utils';
import { TriggerAddEditModalFormValues } from './TriggerAddEditModal.schema';
import { TriggerConditionFormValue } from './TriggerAddEditModal.schema.utils';
import { isTimeCondition } from './TriggerAddEditModal.utils';

/**
 * This function receives the form values and convert it to the set of related fields for the triggers action that the
 * API is expecting.
 *
 * e.g: if actionType is CREATE_JOB, this will return just { actionType: ..., serviceUid: ... } and so on for the other
 * types of actionType.
 */
export const getActionsRelatedFieldsForPayload = ({
  formValues,
  triggerOptions,
}: {
  formValues: TriggerAddEditModalFormValues;
  triggerOptions: GetTriggerOptionsResponseTO | undefined;
}): Pick<
  TriggersCreatePayload,
  | 'actionType'
  | 'jobTiming'
  | 'recipients'
  | 'serviceUid'
  | 'templateName'
  | 'templateUid'
> => {
  const { actionType, recipientsList, templateUid } = formValues;
  // actionType should always be present and of type TriggerActionType given the form validation.
  const common = { actionType: actionType as TriggerActionType };
  const allTemplates = [
    ...(triggerOptions?.option.emailTemplates ?? []),
    ...(triggerOptions?.option.smsTemplates ?? []),
    ...(triggerOptions?.option.whatsAppTemplates ?? []),
  ];

  switch (actionType) {
    case TriggerActionType.EMAIL:
    case TriggerActionType.SMS:
    case TriggerActionType.WHATSAPP:
      return {
        ...common,
        templateName:
          allTemplates.find(({ uid }) => templateUid === uid)?.name ?? '',
        templateUid,
        recipients:
          recipientsList
            ?.filter(isNotSelectAll)
            .map((option) => getRecipientUidAndTypeFromValue(option.value)) ??
          [],
      };
    case TriggerActionType.DIRECT_MESSAGE:
      return {
        ...common,
        templateName:
          allTemplates.find(({ uid }) => templateUid === uid)?.name ?? '',
        templateUid,
      };

    case TriggerActionType.CREATE_JOB:
      return {
        ...common,
        jobTiming: formValues.jobTiming as TriggerJobTiming,
        serviceUid: formValues.serviceUid,
      };

    default:
      return { ...common };
  }
};

const convertFormConditionToConditionPayload = ({
  condition,
  conditionIndex,
  formValues,
}: {
  condition: TriggerConditionFormValue;
  conditionIndex?: number;
  formValues: TriggerAddEditModalFormValues;
}): AllTriggerConditions => {
  // conditionType should always be present and of type ConditionType given the form validation but we should exclude
  // the types that are unreachable through the UI.
  const conditionType = condition.conditionType as Exclude<
    ConditionType,
    ConditionType.NEGATION
  >;

  if (conditionType === ConditionType.CHANNEL) {
    const all = !!condition.channels?.some(isSelectAll);
    const channels = condition.channels
      ?.filter(isNotSelectAll)
      .map((o: BaseOption) => o.value as LeadChannel);

    return {
      all,
      channels: all || !channels ? [] : channels,
      conditionType,
    } satisfies TriggerConditionForChannel;
  }

  if (conditionType === ConditionType.DAY_OF_WEEK) {
    return {
      conditionType,
      daysOfWeek:
        condition.daysOfWeek
          ?.filter(isNotSelectAll)
          .map((option) => option.value as DayOfWeek) ?? [],
    } satisfies TriggerConditionForDaysOfWeek;
  }

  if (conditionType === ConditionType.PROPERTY) {
    const all = !!condition.propertyUids?.some(isSelectAll);
    const propertyUids = extractPropertyUidsFromMultiSelect(
      `conditions.${conditionIndex}.propertyUids`,
      formValues,
    );

    return {
      all,
      conditionType,
      propertyUids: all || !propertyUids ? [] : propertyUids,
    } satisfies TriggerConditionForProperty;
  }

  if (conditionType === ConditionType.GUESTS_NUMBER) {
    return {
      conditionType,
      guests: condition.guests ?? 0,
      operator:
        condition.operator === GuestsNumberOperator.IS_GREATER_THAN
          ? GuestsNumberOperator.IS_GREATER_THAN
          : GuestsNumberOperator.IS_LOWER_THAN,
    } satisfies TriggerConditionForGuestNumber;
  }

  if (conditionType === ConditionType.STAY_LENGTH) {
    return {
      conditionType,
      days: condition.days ?? 0,
      operator:
        condition.operator === StayLengthOperator.IS_LONGER
          ? StayLengthOperator.IS_LONGER
          : StayLengthOperator.IS_SHORTER,
    } satisfies TriggerConditionForStayLength;
  }

  if (conditionType === ConditionType.BOOKING_PAID_IN_FULL) {
    const isNotCompleted = condition.operator === IsCompleted.IS_NOT_COMPLETED;
    return {
      conditionType: isNotCompleted
        ? ConditionType.BOOKING_NOT_PAID_IN_FULL
        : conditionType,
    } satisfies TriggerCondition;
  }

  if (
    conditionType === ConditionType.AFTER_STAY_ENDS ||
    conditionType === ConditionType.AFTER_STAY_STARTS ||
    conditionType === ConditionType.BEFORE_STAY_ENDS ||
    conditionType === ConditionType.BEFORE_STAY_STARTS
  ) {
    let time = condition.timeInMilliSeconds ?? 0;

    switch (condition.timeType) {
      case TimeType.DAYS:
        time = hoursToMilliseconds(time * 24);
        break;
      case TimeType.HOURS:
        time = hoursToMilliseconds(time);
        break;
      case TimeType.MINUTES:
        time = minutesToMilliseconds(time);
        break;
      default:
        break;
    }

    let operator;

    switch (condition.operator) {
      case TimeOperator.LESS_THAN_OR_EQUAL:
      case TimeOperator.EQUAL:
      case TimeOperator.GREATER_THAN_OR_EQUAL:
        operator = condition.operator;
        break;

      default:
        operator = TimeOperator.EQUAL;
        break;
    }

    const backupTime =
      conditionType === ConditionType.BEFORE_STAY_STARTS &&
      operator === TimeOperator.LESS_THAN_OR_EQUAL
        ? {
            backupTimeInMilliSeconds:
              typeof condition.backupTimeInMilliSeconds === 'number'
                ? // convert hours to milliseconds
                  condition.backupTimeInMilliSeconds * 1000 * 60 * 60
                : 0,
          }
        : {};

    return {
      ...backupTime,
      conditionType,
      operator,
      timeInMilliSeconds: time,
    } satisfies TriggerConditionForStay;
  }

  // For any negative operator we should send the TriggerConditionForNegation containing the actual conditionType within it
  if (
    condition.operator === IsSigned.IS_NOT_SIGNED ||
    condition.operator === IsCompleted.IS_NOT_COMPLETED
  ) {
    return {
      condition: { conditionType },
      conditionType: ConditionType.NEGATION,
    } satisfies TriggerConditionForNegation;
  }

  return { conditionType };
};

/**
 * This function receives the form values and convert it to its stringified version, following the `TriggerCondition`
 * interface, given that is what the API is expecting.
 */
export const getConditionFieldForPayload = ({
  formValues,
}: {
  formValues: TriggerAddEditModalFormValues;
}): TriggerCondition => {
  const { conditions, timeCondition } = formValues;

  if (!conditions?.length && !timeCondition.condition) {
    return {
      conditionType: ConditionType.AND,
      conditions: [],
    };
  }

  const allConditions: TriggerCondition = {
    conditionType: ConditionType.AND,
    conditions: [
      ...(formValues.triggerType === TriggerType.TIME_BASED
        ? [
            convertFormConditionToConditionPayload({
              condition: timeCondition.condition,
              formValues,
            }),
          ]
        : []),
      ...(conditions?.map((condition, index) =>
        convertFormConditionToConditionPayload({
          condition,
          conditionIndex: index,
          formValues,
        }),
      ) ?? []),
    ],
  };

  return allConditions;
};

/**
 * This function receives the form values and convert it to its stringified version, following the `TriggerCondition`
 * interface, given that is what the API is expecting.
 */
export const getTriggerEventTypeFieldForPayload = ({
  condition,
  eventType,
}: {
  condition: TriggerCondition;
  eventType: TriggerEventType;
}): TriggerEventType => {
  if (eventType === TriggerEventType.COMPLEX_CONDITION) {
    const timeCondition = condition.conditions?.find(isTimeCondition);

    if (timeCondition) {
      switch (timeCondition.conditionType) {
        case ConditionType.AFTER_STAY_ENDS:
          return TriggerEventType.AFTER_STAY_ENDS;
        case ConditionType.AFTER_STAY_STARTS:
          return TriggerEventType.AFTER_STAY_STARTS;
        case ConditionType.BEFORE_STAY_ENDS:
          return TriggerEventType.BEFORE_STAY_ENDS;
        case ConditionType.BEFORE_STAY_STARTS:
          return TriggerEventType.BEFORE_STAY_STARTS;
        case ConditionType.DAY_OF_WEEK:
          return TriggerEventType.DAY_OF_WEEK;
        default:
          throw new Error('Unreachable code');
      }
    }
  }

  return eventType;
};
